import type {
  BulkSharedCapacityEligibilityResult,
  BulkSharedCapacityItemResult,
  BulkSharedCapacityRequest,
  BulkSharedCapacityResult,
} from './types';

export const BULK_SHARED_CAPACITY_LIMIT = 50;

export type SharedCapacityLoadRef = {
  loadId: number;
  orderId: number;
  loadNumber: string;
};

export type SharedCapacityPhase = 'browse' | 'submitting' | 'result';

export type SharedCapacityIneligibleRow = SharedCapacityLoadRef & {
  message: string;
};

export type SharedCapacitySuccessRow = SharedCapacityLoadRef & {
  capacityGroupName: string | null;
  assignedName: string | null;
  maxBuyRate: number | null;
};

export type SharedCapacityFailureRow = {
  loadId: number | null;
  loadNumber: string;
  message: string;
};

export type BulkSharedCapacityOutcome = {
  successes: SharedCapacitySuccessRow[];
  failures: SharedCapacityFailureRow[];
  assignmentDetails: BulkSharedCapacityItemResult[];
};

const loadIdPrefix = /^(\d+)\s*:/;
const loadWord = /\bLoad (\d+)\b/;

export const loadIdFromSharedCapacityError = (
  message: string,
): number | undefined => {
  const prefixed = loadIdPrefix.exec(message);
  if (prefixed?.[1] !== undefined) {
    return Number(prefixed[1]);
  }

  const named = loadWord.exec(message);
  if (named?.[1] !== undefined) {
    return Number(named[1]);
  }

  return undefined;
};

const loadNumberFor = (
  loads: SharedCapacityLoadRef[],
  loadId: number,
): string => {
  return (
    loads.find((load) => load.loadId === loadId)?.loadNumber ?? `${loadId}`
  );
};

const messagesByLoadId = (
  errors: Array<{ message: string }>,
): { byLoadId: Map<number, string[]>; unmatched: string[] } => {
  const byLoadId = new Map<number, string[]>();
  const unmatched: string[] = [];

  errors.forEach((error) => {
    const loadId = loadIdFromSharedCapacityError(error.message);
    if (loadId === undefined) {
      unmatched.push(error.message);
      return;
    }

    const existing = byLoadId.get(loadId) ?? [];
    existing.push(error.message);
    byLoadId.set(loadId, existing);
  });

  return { byLoadId, unmatched };
};

const joinMessages = (messages: string[] | undefined): string => {
  if (messages === undefined || messages.length === 0) {
    return 'This load cannot be shared.';
  }

  return messages.join(' ');
};

export const buildEligibilityRows = (
  loads: SharedCapacityLoadRef[],
  result: BulkSharedCapacityEligibilityResult,
): {
  eligible: SharedCapacityLoadRef[];
  ineligible: SharedCapacityIneligibleRow[];
} => {
  const loadsById = new Map(loads.map((load) => [load.loadId, load]));
  const { byLoadId, unmatched } = messagesByLoadId(result.errors);
  const eligible = result.eligibleLoadIds.flatMap((loadId) => {
    const load = loadsById.get(loadId);
    return load === undefined ? [] : [load];
  });
  const ineligible = result.failedLoadIds.map((loadId) => {
    const load = loadsById.get(loadId);
    return {
      loadId,
      orderId: load?.orderId ?? 0,
      loadNumber: load?.loadNumber ?? `${loadId}`,
      message: joinMessages(byLoadId.get(loadId)),
    };
  });

  unmatched.forEach((message) => {
    ineligible.push({
      loadId: 0,
      orderId: 0,
      loadNumber: '',
      message,
    });
  });

  return { eligible, ineligible };
};

export const buildBulkSharedCapacityRequests = (
  loads: SharedCapacityLoadRef[],
  eligibleLoadIds: number[],
): BulkSharedCapacityRequest[] => {
  const loadsById = new Map(loads.map((load) => [load.loadId, load]));
  const seen = new Set<number>();
  const requests: BulkSharedCapacityRequest[] = [];

  eligibleLoadIds.forEach((loadId) => {
    if (seen.has(loadId)) {
      return;
    }

    const load = loadsById.get(loadId);
    if (load === undefined || !Number.isFinite(load.orderId)) {
      return;
    }

    seen.add(loadId);
    requests.push({
      loadId,
      orderId: load.orderId,
      removeCapacity: false,
    });
  });

  return requests;
};

export const withSharedCapacitySelection = (
  selectedLoadIds: number[],
  loadId: number,
  checked: boolean,
): number[] => {
  if (!checked) {
    return selectedLoadIds.filter((id) => id !== loadId);
  }

  if (
    selectedLoadIds.includes(loadId) ||
    selectedLoadIds.length >= BULK_SHARED_CAPACITY_LIMIT
  ) {
    return selectedLoadIds;
  }

  return [...selectedLoadIds, loadId];
};

export const selectEligibleSharedCapacity = (
  loadIdsInOrder: number[],
  eligibleLoadIds: number[],
): number[] => {
  const eligible = new Set(eligibleLoadIds);
  const selected: number[] = [];

  loadIdsInOrder.forEach((loadId) => {
    if (
      selected.length >= BULK_SHARED_CAPACITY_LIMIT ||
      !eligible.has(loadId)
    ) {
      return;
    }

    selected.push(loadId);
  });

  return selected;
};

export const canSubmitBulkSharedCapacity = (input: {
  phase: SharedCapacityPhase;
  selectedCount: number;
}): boolean => {
  return (
    input.phase === 'browse' &&
    input.selectedCount > 0 &&
    input.selectedCount <= BULK_SHARED_CAPACITY_LIMIT
  );
};

export const shouldRefreshAfterBulkSharedCapacity = (
  result: Pick<BulkSharedCapacityResult, 'success' | 'partialSuccess'>,
): boolean => {
  return result.success || result.partialSuccess;
};

export const bulkSharedCapacityToast = (
  result: BulkSharedCapacityResult,
  assignedCount: number,
): { type: 'success' | 'none'; message?: string } => {
  if (!result.success) {
    return { type: 'none' };
  }

  const noun = assignedCount === 1 ? 'load' : 'loads';
  return {
    type: 'success',
    message: `${assignedCount} ${noun} assigned to shared capacity`,
  };
};

const uniformValue = <T>(values: T[]): T | null => {
  const first = values[0];
  if (first === undefined) {
    return null;
  }

  return values.every((value) => value === first) ? first : null;
};

export const buildBulkSharedCapacityOutcome = (
  loads: SharedCapacityLoadRef[],
  submittedLoadIds: number[],
  result: BulkSharedCapacityResult,
): BulkSharedCapacityOutcome => {
  const failed = new Set(result.failedLoadIds);
  const { byLoadId, unmatched } = messagesByLoadId(result.errors);
  const successLoadIds = submittedLoadIds.filter(
    (loadId) => !failed.has(loadId),
  );
  const pairedResult =
    successLoadIds.length === 1 && result.results.length === 1
      ? result.results[0]
      : undefined;
  const sharedGroup =
    pairedResult?.capacityGroupName ??
    uniformValue(result.results.map((item) => item.capacityGroupName));
  const sharedAssignee =
    pairedResult?.assignedName ??
    uniformValue(result.results.map((item) => item.assignedName));
  const sharedMaxBuy =
    pairedResult !== undefined
      ? pairedResult.maxBuyRate
      : uniformValue(result.results.map((item) => item.maxBuyRate));

  const successes = successLoadIds.map((loadId) => ({
    loadId,
    orderId: loads.find((load) => load.loadId === loadId)?.orderId ?? 0,
    loadNumber: loadNumberFor(loads, loadId),
    capacityGroupName: sharedGroup,
    assignedName: sharedAssignee,
    maxBuyRate: sharedMaxBuy,
  }));
  const rowsDescribeEveryResult =
    successes.length > 0 &&
    successes.every(
      (row) =>
        row.capacityGroupName !== null &&
        row.assignedName !== null &&
        row.maxBuyRate !== null,
    );

  const failures: SharedCapacityFailureRow[] = result.failedLoadIds.map(
    (loadId) => ({
      loadId,
      loadNumber: loadNumberFor(loads, loadId),
      message: joinMessages(byLoadId.get(loadId)),
    }),
  );

  successLoadIds.forEach((loadId) => {
    const messages = byLoadId.get(loadId);
    if (messages === undefined) {
      return;
    }

    failures.push({
      loadId,
      loadNumber: loadNumberFor(loads, loadId),
      message: messages.join(' '),
    });
  });

  unmatched.forEach((message) => {
    failures.push({
      loadId: null,
      loadNumber: '',
      message,
    });
  });

  const assignmentDetails =
    pairedResult !== undefined || rowsDescribeEveryResult ? [] : result.results;

  return { successes, failures, assignmentDetails };
};
